import styles from "./sidebar.module.scss";
import { NavLink, useHistory } from "react-router-dom";
import { List } from "@material-ui/core";
import classNames from "classnames";
import { Path } from "enums";
import { useCallback, useContext } from "react";
import { AppContext, UserContext } from "contexts";
import { NexusMark } from "images";

const ICONS = {
  dashboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 12h6V4H4v8zm10 8h6v-6h-6v6zM4 20h6v-4H4v4zm10-10h6V4h-6v6z"
      />
    </svg>
  ),
  areaProfiles: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1118 0z"
      />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  groundEnumeration: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18m9-9H3" />
      <circle cx="12" cy="12" r="8" />
    </svg>
  ),
  cohorts: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"
      />
      <circle cx="9" cy="7" r="4" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M23 21v-2a4 4 0 00-3-3.87" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),
  actionCenter: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
    </svg>
  ),
  aiChat: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 15a4 4 0 01-4 4H8l-5 3V7a4 4 0 014-4h10a4 4 0 014 4z"
      />
      <circle cx="9" cy="11" r="1" />
      <circle cx="13" cy="11" r="1" />
      <circle cx="17" cy="11" r="1" />
    </svg>
  ),
};

const NAVIGATION_SECTIONS = [
  {
    section: "LANDSCAPE",
    items: [
      { label: "Landscape Dashboard", iconNode: ICONS.dashboard, disabled: true },
      { label: "Area Profiles", iconNode: ICONS.areaProfiles, disabled: true },
    ],
  },
  {
    section: "INTELLIGENCE",
    items: [
      {
        label: "Ground Enumeration",
        link: Path.Campaigns,
        exact: true,
        iconNode: ICONS.groundEnumeration,
      },
      {
        label: "Cohorts",
        link: Path.Cohorts,
        exact: true,
        iconNode: ICONS.cohorts,
      },
      { label: "Action Center", iconNode: ICONS.actionCenter, disabled: true },
    ],
  },
  {
    section: "ASSISTANT",
    items: [{ label: "AI Chat", iconNode: ICONS.aiChat, disabled: true }],
  },
];

const Sidebar = () => {
  const history = useHistory();
  const { toggleLoading } = useContext(AppContext);
  const { user } = useContext(UserContext);

  const logoutCb = useCallback(async () => {
    try {
      toggleLoading(true);
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("organizationId");
      localStorage.removeItem("campaignCode");
      history.push(Path.Auth);
    } finally {
      toggleLoading(false);
    }
  }, [history, toggleLoading]);

  const displayName = user?.fullName || user?.name || "Portal User";
  const displayEmail = user?.email || "";
  const initials = (displayName || "PU")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return (
    <div className={classNames(styles.consumerSidebarContainer)}>
      <div className={styles.brandSection}>
        <div className={styles.brandIconContainer}>
          <img src={NexusMark} alt="Nexus" className={styles.brandIcon} />
        </div>
        <div>
          <h1 className={styles.brandTitle}>Nexus</h1>
          <p className={styles.brandSubtitle}>Landscape Intelligence</p>
        </div>
      </div>
      <List className={styles.sidebarList}>
        {NAVIGATION_SECTIONS.map((group) => (
          <div key={group.section} className={styles.navGroup}>
            <p className={styles.navGroupLabel}>{group.section}</p>
            {group.items.map((item) =>
              item.disabled ? (
                <div
                  key={item.label}
                  className={classNames(styles.listItem, styles.disabledItem)}
                  aria-disabled="true"
                >
                  <span className={styles.item}>
                    <span className={styles.listIconRoot}>{item.iconNode || null}</span>
                    <span className={styles.listText}>{item.label}</span>
                  </span>
                </div>
              ) : (
                <NavLink
                  key={item.label}
                  to={item.link}
                  exact={item.exact}
                  className={classNames(styles.listItem)}
                  activeClassName={styles.active}
                >
                  <span className={styles.item}>
                    <span className={styles.listIconRoot}>{item.iconNode || null}</span>
                    <span className={styles.listText}>{item.label}</span>
                  </span>
                </NavLink>
              ),
            )}
          </div>
        ))}
      </List>
      <div className={styles.sidebarFooter}>
        <div className={styles.profileCard}>
          <div className={styles.avatar}>{initials}</div>
          <div>
            <p className={styles.profileName}>{displayName}</p>
            <p className={styles.profileEmail}>{displayEmail}</p>
          </div>
        </div>
        <button className={styles.logoutButton} onClick={logoutCb}>
          <svg className={styles.logoutIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"
            />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" x2="9" y1="12" y2="12" />
          </svg>
          Logout
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
